import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './ScrumNotesPanel.css';
import { ScrumNote, Severity, ScrumNoteForm, ScrumNotesPanelProps, RepeatedIssue, MondayItem, IntranetTask, InquiryType } from '../../types';
import DuplicateNotesModal from './DuplicateNotesModal';
import TaskInquiryModal from './TaskInquiryModal';
import { API_URL, extractMondayTaskInfo, getMondayItemUrl } from '../../constants/appConstants';
import { useAuth } from '../../context/AuthContext';

// Helper to format seconds to hours/minutes
const formatTaskTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

interface MondayTaskBadgeProps {
  itemId: string;
  mondayData: Record<string, MondayItem>;
  mondayUrl: string | null;
}

const MondayTaskBadge: React.FC<MondayTaskBadgeProps> = ({ itemId, mondayData, mondayUrl }) => {
  const item = mondayData[itemId];

  if (!item) {
    return mondayUrl ? (
      <a
        href={mondayUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="monday-badge monday-badge-link"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="monday-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        Monday.com
      </a>
    ) : null;
  }

  return (
    <a
      href={mondayUrl || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="monday-task-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="monday-task-header">
        <svg className="monday-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <span className="monday-task-name">{item.name}</span>
      </div>
      <div className="monday-task-details">
        {item.status && (
          <span className="monday-task-status">{item.status}</span>
        )}
        {item.assignee && (
          <span className="monday-task-assignee">{item.assignee}</span>
        )}
        {item.groupName && (
          <span className="monday-task-group">{item.groupName}</span>
        )}
      </div>
    </a>
  );
};

interface IssueTitleProps {
  title: string;
  mondayData: Record<string, MondayItem>;
}

const IssueTitle: React.FC<IssueTitleProps> = ({ title, mondayData }) => {
  const taskInfo = extractMondayTaskInfo(title);
  const mondayUrl = taskInfo ? getMondayItemUrl(taskInfo) : null;

  return (
    <div className="issue-title-wrapper">
      <span className="issue-title-text">{title}</span>
      {taskInfo && (
        <MondayTaskBadge
          itemId={taskInfo.itemId}
          mondayData={mondayData}
          mondayUrl={mondayUrl}
        />
      )}
    </div>
  );
};

const defaultForm: ScrumNoteForm = {
  issueTitle: '',
  details: '',
  severity: 'medium',
  occurredOn: new Date().toISOString().split('T')[0],
  isPeerReview: false
};

type HistoryTab = 'all' | 'resolved';

const severityRank: Record<Severity, number> = {
  low: 1,
  medium: 2,
  high: 3
};

const normalizeIssueKey = (title: string): string => title.trim().toLowerCase();
const getNoteTimestamp = (note: ScrumNote): number =>
  new Date(note.occurred_on || note.created_at).getTime();

interface IntranetDeveloper {
  id: string;
  name: string;
}

const ScrumNotesPanel: React.FC<ScrumNotesPanelProps> = ({ employeeId, employeeName, isAdmin = false, intranetEmployeeId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<ScrumNote[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [form, setForm] = useState(defaultForm);
  const [historyTab, setHistoryTab] = useState<HistoryTab>('all');
  const [duplicateIssueKey, setDuplicateIssueKey] = useState<string | null>(null);
  const [mondayData, setMondayData] = useState<Record<string, MondayItem>>({});
  const [mondayConfigured, setMondayConfigured] = useState<boolean>(false);
  const [intranetTasks, setIntranetTasks] = useState<IntranetTask[]>([]);
  const [completedTasks, setCompletedTasks] = useState<IntranetTask[]>([]);
  const [intranetTasksLoading, setIntranetTasksLoading] = useState<boolean>(false);
  const [showIntranetTasks, setShowIntranetTasks] = useState<boolean>(true);
  const [resolvedIntranetId, setResolvedIntranetId] = useState<string | null>(intranetEmployeeId || null);
  const [inquiryTask, setInquiryTask] = useState<IntranetTask | null>(null);
  const [managerIntranetId, setManagerIntranetId] = useState<string | null>(null);

  // Try to find intranet ID by matching employee name (same logic as PerformancePanel)
  useEffect(() => {
    if (!isAdmin) return;
    
    if (intranetEmployeeId) {
      setResolvedIntranetId(intranetEmployeeId);
      return;
    }

    const findIntranetId = async () => {
      try {
        const response = await axios.get<IntranetDeveloper[]>(`${API_URL}/intranet/developers`, {
          withCredentials: true
        });
        const developers = response.data;
        
        // Manual overrides for employees with name matching issues
        const NAME_TO_ID_OVERRIDES: Record<string, string> = {
          'anna ho': '249',
          'anna': '249',
        };
        
        const normalizedName = employeeName.toLowerCase().trim();
        
        // Check for manual override first
        if (NAME_TO_ID_OVERRIDES[normalizedName]) {
          setResolvedIntranetId(NAME_TO_ID_OVERRIDES[normalizedName]);
          return;
        }
        
        const nameParts = normalizedName.split(' ').filter(p => p.length > 0);
        
        // 1. Exact match (full name)
        let match = developers.find(dev => 
          dev.name.toLowerCase().trim() === normalizedName
        );
        
        // 2. First name + last name match (handles middle names)
        if (!match && nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          match = developers.find(dev => {
            const devParts = dev.name.toLowerCase().trim().split(' ');
            return devParts[0] === firstName && devParts[devParts.length - 1] === lastName;
          });
        }
        
        // 3. Unique first name match
        if (!match && nameParts.length >= 1) {
          const firstName = nameParts[0];
          const matchingFirstNames = developers.filter(dev => 
            dev.name.toLowerCase().trim().split(' ')[0] === firstName
          );
          if (matchingFirstNames.length === 1) {
            match = matchingFirstNames[0];
          }
        }
        
        // 4. Partial match - full name contained
        if (!match && nameParts.length >= 2) {
          match = developers.find(dev => 
            dev.name.toLowerCase().includes(normalizedName) ||
            normalizedName.includes(dev.name.toLowerCase())
          );
        }
        
        if (match) {
          setResolvedIntranetId(match.id);
        }
      } catch (err) {
        console.debug('Could not fetch intranet developers for name matching:', err);
      }
    };

    findIntranetId();
  }, [employeeName, intranetEmployeeId, isAdmin]);

  const checkMondayStatus = useCallback(async () => {
    try {
      const response = await axios.get<{ configured: boolean }>(`${API_URL}/monday/status`);
      setMondayConfigured(response.data.configured);
    } catch {
      setMondayConfigured(false);
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<ScrumNote[]>(`${API_URL}/scrum-notes/employee/${employeeId}`);
      setNotes(response.data);
    } catch (err) {
      console.error('Error fetching scrum notes:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const fetchIntranetTasks = useCallback(async () => {
    if (!resolvedIntranetId || !isAdmin) return;
    
    setIntranetTasksLoading(true);
    try {
      // Calculate yesterday's date in YYYY-MM-DD format
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Fetch yesterday's tasks for scrum review
      const response = await axios.get<IntranetTask[]>(
        `${API_URL}/intranet/tasks/${resolvedIntranetId}/date/${yesterdayStr}`, 
        { withCredentials: true }
      );
      
      // Separate into active (open) and completed tasks
      const tasks = response.data || [];
      const active = tasks.filter(t => !t.completed);
      const completed = tasks.filter(t => t.completed);
      
      setIntranetTasks(active);
      setCompletedTasks(completed);
    } catch (err) {
      console.error('Error fetching intranet tasks:', err);
      setIntranetTasks([]);
      setCompletedTasks([]);
    } finally {
      setIntranetTasksLoading(false);
    }
  }, [resolvedIntranetId, isAdmin]);

  useEffect(() => {
    fetchNotes();
    checkMondayStatus();
  }, [fetchNotes, checkMondayStatus]);

  // Fetch intranet tasks when resolvedIntranetId is set
  useEffect(() => {
    if (resolvedIntranetId && isAdmin) {
      fetchIntranetTasks();
    }
  }, [resolvedIntranetId, isAdmin, fetchIntranetTasks]);

  // Get the manager's (current user's) intranet ID for task inquiries
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchManagerId = async () => {
      try {
        const response = await axios.get<{ id: string; name: string }>(`${API_URL}/intranet/me`, {
          withCredentials: true
        });
        if (response.data?.id) {
          setManagerIntranetId(response.data.id);
        }
      } catch (err) {
        console.debug('Could not fetch manager intranet ID:', err);
      }
    };
    
    fetchManagerId();
  }, [isAdmin]);

  // Handle submitting a task inquiry
  const handleSubmitInquiry = useCallback(async (data: {
    toEmployeeId: string;
    toEmployeeName: string;
    intranetTaskId: string;
    taskDescription: string;
    taskProject: string;
    taskType: string;
    taskTimeSeconds: number;
    inquiryType: InquiryType;
    message: string;
  }) => {
    await axios.post(`${API_URL}/task-inquiries`, {
      fromEmployeeId: managerIntranetId,
      fromEmployeeName: user?.username || 'Manager',
      toEmployeeId: data.toEmployeeId,
      toEmployeeName: data.toEmployeeName,
      intranetTaskId: data.intranetTaskId,
      taskDescription: data.taskDescription,
      taskProject: data.taskProject,
      taskType: data.taskType,
      taskTimeSeconds: data.taskTimeSeconds,
      inquiryType: data.inquiryType,
      message: data.message
    }, { withCredentials: true });
  }, [managerIntranetId, user?.username]);

  // Fetch Monday.com data for notes that have task IDs
  useEffect(() => {
    if (!mondayConfigured || notes.length === 0) return;

    const itemIds = notes
      .map(note => extractMondayTaskInfo(note.issue_title))
      .filter((info): info is NonNullable<typeof info> => info !== null && info.boardId !== null)
      .map(info => info.itemId);

    const uniqueIds = Array.from(new Set(itemIds));

    setMondayData(prev => {
      const missingIds = uniqueIds.filter(id => !prev[id]);
      if (missingIds.length === 0) return prev;

      const fetchMondayItems = async () => {
        try {
          const response = await axios.post<MondayItem[]>(`${API_URL}/monday/items`, { ids: missingIds });
          if (Array.isArray(response.data)) {
            const newData: Record<string, MondayItem> = {};
            for (const item of response.data) {
              if (item) newData[item.id] = item;
            }
            setMondayData(current => ({ ...current, ...newData }));
          }
        } catch (err) {
          // Silently fail - Monday.com integration is optional
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.debug('Monday.com data fetch skipped (API may not be configured)');
          }
          // Disable further Monday.com requests for this session
          setMondayConfigured(false);
        }
      };

      fetchMondayItems();
      return prev;
    });
  }, [notes, mondayConfigured]);

  const handleInputChange = (field: keyof ScrumNoteForm, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Memoize issue grouping map early so it can be used in handlers
  const { issueNotesMap, repeatedIssues } = useMemo((): {
    issueNotesMap: Map<string, ScrumNote[]>;
    repeatedIssues: RepeatedIssue[];
  } => {
    const map = new Map<string, ScrumNote[]>();
    notes.forEach(note => {
      const key = normalizeIssueKey(note.issue_title);
      const existing = map.get(key);
      if (existing) {
        existing.push(note);
      } else {
        map.set(key, [note]);
      }
    });

    const repeated = Array.from(map.values())
      .filter(notes => notes.length > 1 && notes.some(n => n.status !== 'resolved'))
      .map(notes => ({
        title: notes[0].issue_title,
        count: notes.length,
        severity: notes.reduce<Severity>((max, n) => 
          severityRank[n.severity] > severityRank[max] ? n.severity : max,
          notes[0].severity
        )
      }));

    return { issueNotesMap: map, repeatedIssues: repeated };
  }, [notes]);

  const handleAddNote = async () => {
    if (!form.issueTitle.trim()) {
      alert('Issue title is required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/scrum-notes/employee/${employeeId}`, {
        issueTitle: form.issueTitle,
        details: form.details,
        severity: form.severity,
        occurredOn: form.occurredOn,
        isPeerReview: form.isPeerReview
      });
      setForm(defaultForm);
      fetchNotes();
    } catch (err) {
      console.error('Error creating scrum note:', err);
      alert('Failed to save scrum note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (note: ScrumNote) => {
    const nextStatus = note.status === 'open' ? 'resolved' : 'open';
    const issueKey = normalizeIssueKey(note.issue_title);
    const duplicateNotes = issueNotesMap.get(issueKey) ?? [];
    const hasRepeatedIssues = duplicateNotes.length > 1;
    
    // When resolving a repeated issue, resolve all related notes
    if (nextStatus === 'resolved' && hasRepeatedIssues) {
      try {
        const response = await axios.put<{ updatedIds: number[]; status: string }>(
          `${API_URL}/scrum-notes/employee/${employeeId}/bulk-status`,
          { issueTitle: note.issue_title, status: nextStatus }
        );
        
        const updatedIds = response.data.updatedIds || duplicateNotes.map(n => n.id);
        setNotes(prev =>
          prev.map(n => 
            updatedIds.includes(n.id) ? { ...n, status: nextStatus } : n
          )
        );
      } catch (err) {
        console.error('Error bulk updating scrum note status:', err);
        alert('Failed to resolve all related notes');
      }
    } else {
      // Single note update
      try {
        await axios.put(`${API_URL}/scrum-notes/${note.id}/status`, { status: nextStatus });
        setNotes(prev =>
          prev.map(n => (n.id === note.id ? { ...n, status: nextStatus } : n))
        );
      } catch (err) {
        console.error('Error updating scrum note status:', err);
        alert('Failed to update status');
      }
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Delete this scrum note?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/scrum-notes/${noteId}`);
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err) {
      console.error('Error deleting scrum note:', err);
      alert('Failed to delete scrum note');
    }
  };

  const { openIssues, resolvedIssues } = useMemo(() => {
    const open: ScrumNote[] = [];
    const resolved: ScrumNote[] = [];
    notes.forEach(note => {
      if (note.status === 'resolved') {
        resolved.push(note);
      } else {
        open.push(note);
      }
    });
    return { openIssues: open, resolvedIssues: resolved };
  }, [notes]);

  useEffect(() => {
    if (historyTab === 'resolved' && resolvedIssues.length === 0) {
      setHistoryTab('all');
    }
  }, [historyTab, resolvedIssues.length]);

  const showAllHistory = useCallback(() => setHistoryTab('all'), []);
  const showResolvedHistory = useCallback(() => setHistoryTab('resolved'), []);
  const openDuplicateModal = useCallback((issueKey: string) => {
    setDuplicateIssueKey(issueKey);
  }, []);
  const closeDuplicateModal = useCallback(() => setDuplicateIssueKey(null), []);

  const displayedNotes = historyTab === 'resolved' ? resolvedIssues : openIssues;
  const historyEmptyStateCopy =
    historyTab === 'resolved'
      ? 'No resolved scrum notes yet. Mark an issue as resolved to see it here.'
      : notes.length === 0
      ? 'No scrum notes yet. Use the form above to log the first issue.'
      : 'No open scrum notes right now. Everything is currently resolved.';

  const deduplicatedDisplayedNotes = useMemo(() => {
    const latestByIssue = new Map<string, ScrumNote>();

    displayedNotes.forEach(note => {
      const key = normalizeIssueKey(note.issue_title);
      const existing = latestByIssue.get(key);

      if (!existing || getNoteTimestamp(note) >= getNoteTimestamp(existing)) {
        latestByIssue.set(key, note);
      }
    });

    return Array.from(latestByIssue.values()).sort(
      (a, b) => getNoteTimestamp(b) - getNoteTimestamp(a)
    );
  }, [displayedNotes]);

  const duplicateModalNotes = duplicateIssueKey
    ? issueNotesMap.get(duplicateIssueKey) ?? []
    : [];
  const showDuplicateModal = duplicateIssueKey !== null && duplicateModalNotes.length > 0;
  const duplicateModalTitle = showDuplicateModal ? duplicateModalNotes[0].issue_title : '';

  return (
    <div className="scrum-panel">
      <div className="scrum-summary">
        <div className="scrum-summary-card">
          <span className="scrum-summary-label">Open Issues</span>
          <span className="scrum-summary-value">{openIssues.length}</span>
        </div>
        <div className="scrum-summary-card">
          <span className="scrum-summary-label">Resolved</span>
          <span className="scrum-summary-value">{resolvedIssues.length}</span>
        </div>
        <div className="scrum-summary-card">
          <span className="scrum-summary-label">Repeated Issues</span>
          <span className="scrum-summary-value">{repeatedIssues.length}</span>
        </div>
      </div>

      {repeatedIssues.length > 0 && (
        <div className="repeated-issues">
          <h4>Patterns detected</h4>
          <div className="repeated-issue-tags">
            {repeatedIssues.map(issue => (
              <div key={issue.title} className={`issue-tag severity-${issue.severity}`}>
                <span>{issue.title}</span>
                <span className="issue-count">{issue.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="scrum-form-card">
          <h3>Log Scrum Note</h3>
          <div className="scrum-form">
            <div className="form-group">
              <label>Issue Title</label>
              <input
                type="text"
                value={form.issueTitle}
                onChange={(e) => handleInputChange('issueTitle', e.target.value)}
                placeholder="i.e. Missing code documentation"
              />
            </div>
            <div className="form-group">
              <label>Details</label>
              <textarea
                value={form.details}
                onChange={(e) => handleInputChange('details', e.target.value)}
                rows={3}
                placeholder={`What happened during stand-up with ${employeeName}?`}
              />
            </div>
            <div className="scrum-form-row">
              <div className="form-group">
                <label>Severity</label>
                <select
                  value={form.severity}
                  onChange={(e) => handleInputChange('severity', e.target.value as Severity)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={form.occurredOn}
                  onChange={(e) => handleInputChange('occurredOn', e.target.value)}
                />
              </div>
              <div className="form-group peer-review-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.isPeerReview}
                    onChange={(e) => handleInputChange('isPeerReview', e.target.checked)}
                  />
                  <span className="checkbox-text">Peer Review</span>
                </label>
              </div>
              <div className="form-group align-end">
                <button
                  className="btn btn-primary"
                  onClick={handleAddNote}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Add Scrum Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Intranet Tasks Section - Only show for admin with resolved intranet employee ID */}
      {isAdmin && resolvedIntranetId && (
        <div className="intranet-tasks-section">
          <div className="intranet-tasks-header">
            <div className="intranet-tasks-title-row">
              <h3>Yesterday's Intranet Tasks</h3>
              <span className="intranet-tasks-count">
                {intranetTasks.length + completedTasks.length} tasks
              </span>
            </div>
            <button
              type="button"
              className={`intranet-tasks-toggle ${showIntranetTasks ? 'expanded' : 'collapsed'}`}
              onClick={() => setShowIntranetTasks(!showIntranetTasks)}
            >
              {showIntranetTasks ? '▼ Hide' : '▶ Show'}
            </button>
          </div>
          
          {showIntranetTasks && (
            <>
              {intranetTasksLoading ? (
                <div className="intranet-tasks-loading">Loading intranet tasks...</div>
              ) : (intranetTasks.length === 0 && completedTasks.length === 0) ? (
                <div className="intranet-tasks-empty">No intranet tasks found for yesterday.</div>
              ) : (
                <div className="intranet-tasks-list">
                  {/* Active/Open Tasks */}
                  {intranetTasks.map(task => (
                    <div key={task.id} className={`intranet-task-card ${task.isRunning ? 'running' : ''}`}>
                      <div className="intranet-task-row">
                        <div className="intranet-task-status-indicator">
                          {task.isRunning ? (
                            <span className="status-dot running" title="Timer Running">●</span>
                          ) : (
                            <span className="status-dot open" title="Open">○</span>
                          )}
                        </div>
                        <div className="intranet-task-content">
                          <div className="intranet-task-description">{task.description || 'No description'}</div>
                          <div className="intranet-task-meta">
                            <span className="intranet-task-project">{task.projectName || task.company || 'Internal'}</span>
                            <span className={`intranet-task-type type-${(task.type || 'project').toLowerCase()}`}>{task.type || 'PROJECT'}</span>
                            <span className="intranet-task-time">{formatTaskTime(task.timeTotal)}</span>
                            {task.timeEstimate > 0 && (
                              <span className="intranet-task-estimate">Est: {formatTaskTime(task.timeEstimate)}</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="intranet-task-ask-btn"
                          onClick={() => setInquiryTask(task)}
                          title="Ask about this task"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Completed Tasks */}
                  {completedTasks.map(task => (
                    <div key={task.id} className="intranet-task-card completed">
                      <div className="intranet-task-row">
                        <div className="intranet-task-status-indicator">
                          <span className="status-dot completed" title="Completed">✓</span>
                        </div>
                        <div className="intranet-task-content">
                          <div className="intranet-task-description">{task.description || 'No description'}</div>
                          <div className="intranet-task-meta">
                            <span className="intranet-task-project">{task.projectName || task.company || 'Internal'}</span>
                            <span className={`intranet-task-type type-${(task.type || 'project').toLowerCase()}`}>{task.type || 'PROJECT'}</span>
                            <span className="intranet-task-time">{formatTaskTime(task.timeTotal)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="intranet-task-ask-btn"
                          onClick={() => setInquiryTask(task)}
                          title="Ask about this task"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="scrum-notes-list">
        <div className="list-header">
          <div className="list-header-text">
            <h3>Scrum History</h3>
            <span className="list-subtitle">
              {historyTab === 'resolved'
                ? `${resolvedIssues.length} resolved`
                : `${openIssues.length} open`}
            </span>
          </div>
          <div className="history-tabs">
            <button
              type="button"
              className={`history-tab-btn ${historyTab === 'all' ? 'active' : ''}`}
              onClick={showAllHistory}
              disabled={historyTab === 'all'}
            >
              All ({openIssues.length})
            </button>
            {resolvedIssues.length > 0 && (
              <button
                type="button"
                className={`history-tab-btn ${historyTab === 'resolved' ? 'active' : ''}`}
                onClick={showResolvedHistory}
                disabled={historyTab === 'resolved'}
              >
                Resolved ({resolvedIssues.length})
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="scrum-loading">Loading scrum notes...</div>
        ) : displayedNotes.length === 0 ? (
          <div className="scrum-empty-state">{historyEmptyStateCopy}</div>
        ) : (
          deduplicatedDisplayedNotes.map(note => {
            const issueKey = normalizeIssueKey(note.issue_title);
            const duplicateNotes = issueNotesMap.get(issueKey) ?? [];
            const repeatCount = duplicateNotes.length;

            return (
              <div key={note.id} className="scrum-note-card">
                <div className="scrum-note-header">
                  <div>
                    <div className="scrum-note-meta">
                      <span className={`scrum-severity severity-${note.severity}`}>{note.severity}</span>
                      {note.is_peer_review ? (
                        <span className="peer-review-badge">Peer Review</span>
                      ) : null}
                      {repeatCount > 1 && (
                        <button
                          type="button"
                          className="repeat-count-badge"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDuplicateModal(issueKey);
                          }}
                        >
                          Repeated {repeatCount}x
                        </button>
                      )}
                    </div>
                    <h4><IssueTitle title={note.issue_title} mondayData={mondayData} /></h4>
                    <p className="scrum-note-date">
                      {new Date(note.occurred_on || note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="scrum-note-actions">
                    {isAdmin ? (
                      <>
                        <button
                          className={`status-chip ${note.status === 'resolved' ? 'resolved' : 'open'}`}
                          onClick={() => handleStatusToggle(note)}
                        >
                          {note.status === 'resolved' ? 'Resolved' : 'Open'}
                        </button>
                        <button className="delete-point-btn" onClick={() => handleDeleteNote(note.id)}>
                          ×
                        </button>
                      </>
                    ) : (
                      <span className={`status-chip ${note.status === 'resolved' ? 'resolved' : 'open'}`}>
                        {note.status === 'resolved' ? 'Resolved' : 'Open'}
                      </span>
                    )}
                  </div>
                </div>
                {note.details && <p className="scrum-note-details">{note.details}</p>}
              </div>
            );
          })
        )}
      </div>
      {showDuplicateModal && (
        <DuplicateNotesModal
          issueTitle={duplicateModalTitle}
          notes={duplicateModalNotes}
          onClose={closeDuplicateModal}
          isAdmin={isAdmin}
          onDelete={async (noteId: number) => {
            if (!window.confirm('Delete this scrum note?')) {
              return;
            }
            try {
              await axios.delete(`${API_URL}/scrum-notes/${noteId}`);
              setNotes(prev => {
                const updated = prev.filter(note => note.id !== noteId);
                // Close modal if no notes left for this issue
                const remainingForIssue = updated.filter(
                  n => normalizeIssueKey(n.issue_title) === duplicateIssueKey
                );
                if (remainingForIssue.length === 0) {
                  setDuplicateIssueKey(null);
                }
                return updated;
              });
            } catch (err) {
              console.error('Error deleting scrum note:', err);
              alert('Failed to delete scrum note');
            }
          }}
        />
      )}
      {inquiryTask && resolvedIntranetId && managerIntranetId && (
        <TaskInquiryModal
          task={inquiryTask}
          toEmployeeId={resolvedIntranetId}
          toEmployeeName={employeeName}
          fromEmployeeId={managerIntranetId}
          fromEmployeeName={user?.username || 'Manager'}
          onSubmit={handleSubmitInquiry}
          onClose={() => setInquiryTask(null)}
        />
      )}
    </div>
  );
};

export default ScrumNotesPanel;


